import { ActionIcon, Button, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "../SvgIconRaw";
import { downloadTableCsv } from "./csvExport";
import type {
  MeqRowData,
  MeqTableInstance,
  MeqTableLocalization,
} from "./types";

/**
 * CSV export button — old wrapper's `ExportDataToCsvButton` UI parity
 * (tooltip + icon, disabled when there are zero rows) plus a real built-in
 * CSV: all columns by default (old-wrapper parity — visibility ignored),
 * visible-only via `shouldExportVisibleColumnsOnly`; formatted values
 * (`meqInstance.getCsvExportData` -> csvExport.ts, ported export-to-csv
 * mkConfig/generateCsv call-site pattern). `onExport`, if provided,
 * REPLACES the built-in CSV (escape hatch
 * for bespoke per-page logic, matching the old wrapper's caller-supplied
 * `handleExportDataToCsv` exactly).
 */

export interface ExportCsvButtonProps<TData extends MeqRowData> {
  hasBorderedControls?: boolean;
  meqInstance: MeqTableInstance<TData>;
  isIcon?: boolean;
  fileName?: string;
  onExport?: () => void;
  localization?: Partial<MeqTableLocalization>;
}

const ExportCsvButton = <TData extends MeqRowData>({
  meqInstance,
  isIcon = true,
  fileName = "table-export",
  onExport,
  localization,
  hasBorderedControls = false,
}: ExportCsvButtonProps<TData>) => {
  const { t } = useTranslation();
  const exportLabel =
    localization?.exportAllDataToCsv ?? t("table.exportAllDataToCsv");
  const isDisabled = meqInstance.getPrePaginationRowCount() === 0;

  const handleClick = () => {
    if (onExport) {
      onExport();
      return;
    }
    const { columns, rows } = meqInstance.getCsvExportData();
    downloadTableCsv(fileName, columns, rows);
  };

  if (isIcon) {
    return (
      <Tooltip label={exportLabel}>
        <ActionIcon
          variant={hasBorderedControls ? "secondary" : "subtle"}
          size={hasBorderedControls ? "input-sm" : undefined}
          color="gray"
          aria-label={exportLabel}
          disabled={isDisabled}
          onClick={handleClick}
        >
          <SvgIconRaw name="Download" size={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="subtle"
      color="gray"
      size="xs"
      leftSection={<SvgIconRaw name="Download" size={16} />}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {exportLabel}
    </Button>
  );
};

export default ExportCsvButton;
